import type { Vec2 } from '@/geometry/vec2';
import { dist2, lerp2, sub2, dot2, lenSq2 } from '@/geometry/vec2';
import { segmentIntersection } from '@/geometry/intersect';
import { closestPointOnSegment } from '@/geometry/intersect';
import { buildPlanarTopology } from '@/geometry/dcel';
import { VERTEX_MERGE_EPSILON, GEOMETRY_EPSILON } from '@/constants/numeric';
import type { CreaseAssignment, CreasePattern, CreasePatternMeta } from './types';

/**
 * A point registry that merges coincident vertices using a uniform spatial hash.
 * Two points within VERTEX_MERGE_EPSILON collapse to one index, which is what
 * stops the editor accumulating near-duplicate vertices at crease crossings.
 */
export class PointRegistry {
  private readonly cell: number;
  private readonly buckets = new Map<string, number[]>();
  readonly points: Vec2[] = [];
  mergeCount = 0;

  constructor(tolerance = VERTEX_MERGE_EPSILON) {
    this.cell = Math.max(tolerance, Number.MIN_VALUE) * 2;
  }

  private key(ix: number, iy: number): string {
    return `${ix},${iy}`;
  }

  find(p: Vec2): number {
    const ix = Math.floor(p.x / this.cell);
    const iy = Math.floor(p.y / this.cell);
    let best = -1;
    let bestD = Infinity;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const bucket = this.buckets.get(this.key(ix + dx, iy + dy));
        if (!bucket) continue;
        for (const idx of bucket) {
          const d = dist2(this.points[idx], p);
          if (d < bestD) {
            bestD = d;
            best = idx;
          }
        }
      }
    }
    return bestD <= this.cell / 2 ? best : -1;
  }

  add(p: Vec2): number {
    const existing = this.find(p);
    if (existing >= 0) {
      this.mergeCount++;
      return existing;
    }
    const idx = this.points.length;
    this.points.push({ x: p.x, y: p.y });
    const ix = Math.floor(p.x / this.cell);
    const iy = Math.floor(p.y / this.cell);
    const k = this.key(ix, iy);
    const bucket = this.buckets.get(k);
    if (bucket) bucket.push(idx);
    else this.buckets.set(k, [idx]);
    return idx;
  }
}

export interface PlanarizeInput {
  verticesCoords: readonly Vec2[];
  edgesVertices: readonly (readonly [number, number])[];
  edgesAssignment: readonly CreaseAssignment[];
  edgesFoldAngle?: readonly (number | null)[];
  meta?: CreasePatternMeta;
}

export interface PlanarizeReport {
  mergedVertices: number;
  createdVertices: number;
  splitEvents: number;
  duplicateEdgesMerged: number;
  degenerateEdgesDropped: number;
  assignmentConflicts: { edge: [number, number]; kept: CreaseAssignment; dropped: CreaseAssignment }[];
}

export interface PlanarizeResult {
  pattern: CreasePattern;
  report: PlanarizeReport;
}

/**
 * Assignment precedence when two drawn creases end up on the same sub-edge.
 * Higher wins. Boundary and cut are structural so they dominate; M/V beat the
 * "no opinion" assignments; auxiliary loses to everything.
 */
const PRECEDENCE: Record<CreaseAssignment, number> = {
  B: 6,
  C: 5,
  M: 4,
  V: 4,
  F: 3,
  U: 2,
  J: 1,
};

/**
 * Turn an arbitrary set of line segments into a planar embedded graph:
 * every crossing becomes a vertex, every edge is split at every vertex lying on
 * it, duplicate edges are merged, and the face cycles are re-derived.
 *
 * Complexity is O(E^2 + V*E) - a sweep-line would be asymptotically better, but
 * crease patterns drawn by hand are small, and the quadratic version is far
 * easier to make robust. The pairwise loop is the documented scaling limit.
 */
export function planarize(input: PlanarizeInput): PlanarizeResult {
  const registry = new PointRegistry(VERTEX_MERGE_EPSILON);
  const report: PlanarizeReport = {
    mergedVertices: 0,
    createdVertices: 0,
    splitEvents: 0,
    duplicateEdgesMerged: 0,
    degenerateEdgesDropped: 0,
    assignmentConflicts: [],
  };

  const vertexMap = input.verticesCoords.map((p) => registry.add(p));
  const inputVertexCount = registry.points.length;

  interface WorkEdge {
    a: number;
    b: number;
    assignment: CreaseAssignment;
    foldAngle: number | null;
    splits: { t: number; v: number }[];
  }

  const work: WorkEdge[] = [];
  for (let e = 0; e < input.edgesVertices.length; e++) {
    const a = vertexMap[input.edgesVertices[e][0]];
    const b = vertexMap[input.edgesVertices[e][1]];
    if (a === undefined || b === undefined) continue;
    if (a === b) {
      report.degenerateEdgesDropped++;
      continue;
    }
    if (dist2(registry.points[a], registry.points[b]) < VERTEX_MERGE_EPSILON) {
      report.degenerateEdgesDropped++;
      continue;
    }
    work.push({
      a,
      b,
      assignment: input.edgesAssignment[e] ?? 'U',
      foldAngle: input.edgesFoldAngle?.[e] ?? null,
      splits: [
        { t: 0, v: a },
        { t: 1, v: b },
      ],
    });
  }

  const paramOn = (edge: WorkEdge, p: Vec2): number => {
    const A = registry.points[edge.a];
    const B = registry.points[edge.b];
    const d = sub2(B, A);
    const l2 = lenSq2(d);
    if (l2 < GEOMETRY_EPSILON) return 0;
    return dot2(sub2(p, A), d) / l2;
  };

  // 1. Pairwise crossings.
  for (let i = 0; i < work.length; i++) {
    const ei = work[i];
    const p1 = registry.points[ei.a];
    const p2 = registry.points[ei.b];
    for (let j = i + 1; j < work.length; j++) {
      const ej = work[j];
      const q1 = registry.points[ej.a];
      const q2 = registry.points[ej.b];
      const hit = segmentIntersection(p1, p2, q1, q2);
      if (hit.kind === 'none') continue;
      if (hit.kind === 'point' && hit.point) {
        const v = registry.add(hit.point);
        ei.splits.push({ t: paramOn(ei, registry.points[v]), v });
        ej.splits.push({ t: paramOn(ej, registry.points[v]), v });
        report.splitEvents++;
      } else if (hit.kind === 'overlap' && hit.overlap) {
        for (const t of hit.overlap) {
          const p = lerp2(p1, p2, t);
          const v = registry.add(p);
          ei.splits.push({ t, v });
          ej.splits.push({ t: paramOn(ej, registry.points[v]), v });
        }
        report.splitEvents++;
      }
    }
  }

  // 2. Vertices lying in the interior of an edge (T-junctions).
  const vertexCountAfterCrossings = registry.points.length;
  for (const edge of work) {
    const A = registry.points[edge.a];
    const B = registry.points[edge.b];
    const already = new Set(edge.splits.map((s) => s.v));
    for (let v = 0; v < vertexCountAfterCrossings; v++) {
      if (already.has(v)) continue;
      const { distance, t } = closestPointOnSegment(registry.points[v], A, B);
      if (distance <= VERTEX_MERGE_EPSILON && t > 0 && t < 1) {
        edge.splits.push({ t, v });
        already.add(v);
        report.splitEvents++;
      }
    }
  }

  // 3. Emit sub-edges.
  const subEdges = new Map<
    string,
    { a: number; b: number; assignment: CreaseAssignment; foldAngle: number | null }
  >();

  for (const edge of work) {
    edge.splits.sort((s1, s2) => s1.t - s2.t);
    const chain: number[] = [];
    for (const s of edge.splits) {
      if (chain.length === 0 || chain[chain.length - 1] !== s.v) chain.push(s.v);
    }
    for (let k = 0; k + 1 < chain.length; k++) {
      const a = chain[k];
      const b = chain[k + 1];
      if (a === b) continue;
      if (dist2(registry.points[a], registry.points[b]) < VERTEX_MERGE_EPSILON) {
        report.degenerateEdgesDropped++;
        continue;
      }
      const key = a < b ? `${a}_${b}` : `${b}_${a}`;
      const existing = subEdges.get(key);
      if (!existing) {
        subEdges.set(key, {
          a,
          b,
          assignment: edge.assignment,
          foldAngle: edge.foldAngle,
        });
      } else {
        report.duplicateEdgesMerged++;
        const winner =
          PRECEDENCE[edge.assignment] > PRECEDENCE[existing.assignment]
            ? edge.assignment
            : existing.assignment;
        if (
          existing.assignment !== edge.assignment &&
          PRECEDENCE[existing.assignment] === PRECEDENCE[edge.assignment]
        ) {
          report.assignmentConflicts.push({
            edge: [a, b],
            kept: existing.assignment,
            dropped: edge.assignment,
          });
        }
        if (winner !== existing.assignment) {
          existing.assignment = winner;
          existing.foldAngle = edge.foldAngle;
        }
      }
    }
  }

  // 4. Drop vertices that ended up unused, keeping index order stable.
  const used = new Set<number>();
  for (const se of subEdges.values()) {
    used.add(se.a);
    used.add(se.b);
  }
  const remap = new Int32Array(registry.points.length).fill(-1);
  const coords: Vec2[] = [];
  for (let v = 0; v < registry.points.length; v++) {
    if (!used.has(v)) continue;
    remap[v] = coords.length;
    coords.push(registry.points[v]);
  }

  const edgesVertices: [number, number][] = [];
  const edgesAssignment: CreaseAssignment[] = [];
  const edgesFoldAngle: (number | null)[] = [];
  for (const se of subEdges.values()) {
    edgesVertices.push([remap[se.a], remap[se.b]]);
    edgesAssignment.push(se.assignment);
    edgesFoldAngle.push(se.foldAngle);
  }

  report.mergedVertices = registry.mergeCount;
  report.createdVertices = registry.points.length - inputVertexCount;

  const topology = buildPlanarTopology(coords, edgesVertices);

  const pattern: CreasePattern = {
    verticesCoords: coords,
    edgesVertices,
    edgesAssignment,
    edgesFoldAngle,
    facesVertices: topology.faces,
    meta: { ...(input.meta ?? {}) },
  };

  return { pattern, report };
}
